import React, { useEffect, lazy } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Avatar } from '../components/Avatar';
import { CtaButton } from '../components/CtaButton';
const DEVICES = 'https://cdn.magicpatterns.com/uploads/qUvbuX2eVZvg4FTyVpFap9/aa_1.png';
const LOGO = 'https://cdn.magicpatterns.com/uploads/g3xSNcTwxUKYvMqytRpxH8/Frame_2121457531.png';
const PHONE = 'https://cdn.magicpatterns.com/uploads/hCg5ab4aDmKVZjRejyeoeb/Group_26.png';
export function FlowKeyCaseStudy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <>
      <div className="glow" data-id="element-383"></div>
      <Navbar data-id="element-384" />

      <main className="wrap" data-id="element-385">
        <section className="sec pb-0" data-id="element-386">
          <Link to="/#work" className="mono text-xs uppercase tracking-wider text-mut hover:text-lime transition-colors inline-block mb-10" data-id="element-387">
            ← All work
          </Link>
          <div className="eyebrow mb-6" data-id="element-388">Case Study</div>
          <h1 className="text-6xl md:text-8xl font-bold tracking-tight mb-8" data-id="element-389">
            FlowKey Labs
          </h1>
          <p className="text-xl md:text-2xl text-mut max-w-3xl leading-relaxed" data-id="element-390">
            An all-in-one platform that helps local service businesses run
            smoothly — from bookings and payments to staff and client
            management. We built the identity and the product experience around
            one line: streamline, track, grow.
          </p>
        </section>

        {/* Cover — device showcase */}
        <section className="sec" data-id="element-391">
          <figure className="border border-line-2 rounded-r-lg overflow-hidden bg-white" data-id="element-392">
            <img src={DEVICES} alt="FlowKey shown across laptop, phone and tablet — Run your service business smoothly" className="w-full h-auto block" loading="lazy" data-id="element-393" />
          </figure>
          <figcaption className="mono text-xs uppercase tracking-wider text-mut-2 mt-4" data-id="element-394">
            One platform — web, tablet & mobile
          </figcaption>
        </section>

        <section className="sec pt-0" data-id="element-395">
          <div className="stats" data-id="element-396">
            <div className="stat" data-id="element-397">
              <div className="text-xl font-bold text-ink mb-2" data-id="element-398">
                FlowKey Labs
              </div>
              <div className="l" data-id="element-399">Client</div>
            </div>
            <div className="stat" data-id="element-400">
              <div className="text-xl font-bold text-ink mb-2" data-id="element-401">
                SaaS · Service Ops
              </div>
              <div className="l" data-id="element-402">Industry</div>
            </div>
            <div className="stat" data-id="element-403">
              <div className="text-xl font-bold text-ink mb-2" data-id="element-404">6 weeks</div>
              <div className="l" data-id="element-405">Timeline</div>
            </div>
            <div className="stat" data-id="element-406">
              <div className="text-xl font-bold text-ink mb-2" data-id="element-407">
                Identity · Web · UI/UX
              </div>
              <div className="l" data-id="element-408">Services</div>
            </div>
          </div>
        </section>

        <section className="sec" data-id="element-409">
          <div className="studio" data-id="element-410">
            <div data-id="element-411">
              <h2 className="text-4xl md:text-5xl font-bold mb-8" data-id="element-412">
                The Challenge — service businesses run on{' '}
                <span className="serif" data-id="element-413">spreadsheets</span>
              </h2>
            </div>
            <div className="text-lg text-mut leading-relaxed space-y-6" data-id="element-414">
              <p data-id="element-415">
                Local service businesses — studios, clinics, salons, coaches —
                juggle bookings, payments, staff and client communication across
                a patchwork of notebooks, WhatsApp threads and spreadsheets.
                It's slow, error-prone, and it steals the hours owners would
                rather spend serving clients.
              </p>
              <p data-id="element-416">
                FlowKey wanted to replace all of it with one calm, dependable
                platform. The brand had to feel modern and trustworthy enough
                for money to flow through it, while staying approachable for
                small teams who aren't "software people."
              </p>
            </div>
          </div>
        </section>

        <section className="sec" data-id="element-417">
          <div className="studio" data-id="element-418">
            <div data-id="element-419">
              <h2 className="text-4xl md:text-5xl font-bold mb-8" data-id="element-420">
                The Approach — one confident,{' '}
                <span className="serif" data-id="element-421">green</span> system
              </h2>
            </div>
            <div className="text-lg text-mut leading-relaxed space-y-6" data-id="element-422">
              <p data-id="element-423">
                We built the identity around a sharp forward-leaning "F" — a
                mark that reads as motion and momentum. A single, confident
                green carries the whole system: growth, money in motion, and a
                fresh take on ops software that usually looks grey and dull.
                That palette runs straight from the wordmark into the marketing
                site and the product UI, so a business owner sees the same
                FlowKey everywhere.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-4" data-id="element-424">
                <div className="flex items-center gap-3" data-id="element-425">
                  <span className="w-9 h-9 rounded-full border border-line-2" style={{
                  background: '#2fa84f'
                }} aria-hidden="true" data-id="element-426" />
                  <span className="mono text-xs uppercase tracking-wider text-mut" data-id="element-427">
                    FlowKey Green · growth
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-428">
                  <span className="w-9 h-9 rounded-full border border-line-2" style={{
                  background: '#0f1e17'
                }} aria-hidden="true" data-id="element-429" />
                  <span className="mono text-xs uppercase tracking-wider text-mut" data-id="element-430">
                    Ink · clarity
                  </span>
                </div>
              </div>

              <div className="mt-8 space-y-4" data-id="element-431">
                <div className="p-6 border border-line rounded-r-lg bg-raise" data-id="element-432">
                  <h4 className="font-bold text-ink mb-2" data-id="element-433">
                    Everything in one place
                  </h4>
                  <p className="text-base" data-id="element-434">
                    Bookings, payments and client communication live together —
                    no more spreadsheet juggling.
                  </p>
                </div>
                <div className="p-6 border border-line rounded-r-lg bg-raise" data-id="element-435">
                  <h4 className="font-bold text-ink mb-2" data-id="element-436">Track & grow</h4>
                  <p className="text-base" data-id="element-437">
                    A live view of sessions and net income, so owners can focus
                    on serving clients, not admin headaches.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="sec" data-id="element-438">
          <figure className="quote max-w-4xl mx-auto" data-id="element-439">
            <blockquote className="q" data-id="element-440">
              "Yetu turned a pile of spreadsheets into a product our customers
              actually love. FlowKey finally{' '}
              <em data-id="element-441">looks and works like one calm platform</em>."
            </blockquote>
            <figcaption className="who" data-id="element-442">
              <Avatar name="Doris" data-id="element-443" />
              <div data-id="element-444">
                <b data-id="element-445">Doris</b>
                <span data-id="element-446">CEO, FlowKey Labs</span>
              </div>
            </figcaption>
          </figure>
        </section>

        <section className="sec" data-id="element-447">
          <div className="sec__head mb-12" data-id="element-448">
            <div data-id="element-449">
              <div className="eyebrow mb-5" data-id="element-450">Selected screens</div>
              <h2 data-id="element-451">
                Built to <span className="serif" data-id="element-452">streamline</span>, track &
                grow.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch" data-id="element-453">
            {/* Product app — phone with floating UI */}
            <figure data-id="element-454">
              <div className="border border-line rounded-r-lg overflow-hidden bg-white h-full flex items-center justify-center p-8 min-h-[360px]" data-id="element-455">
                <img src={PHONE} alt="FlowKey mobile app showing upcoming sessions and net income" className="w-full h-auto max-w-[420px] block" loading="lazy" data-id="element-456" />
              </div>
              <figcaption className="mono text-xs uppercase tracking-wider text-mut-2 mt-4 text-center" data-id="element-457">
                Product app — bookings & income
              </figcaption>
            </figure>

            {/* Logo lockup on brand green */}
            <figure data-id="element-458">
              <div className="border border-line rounded-r-lg overflow-hidden h-full flex items-center justify-center p-10 min-h-[360px]" style={{
              background: '#0f1e17'
            }} data-id="element-459">
                <img src={LOGO} alt="FlowKey wordmark with the forward-leaning F mark" className="max-w-[70%] h-auto" loading="lazy" data-id="element-460" />
              </div>
              <figcaption className="mono text-xs uppercase tracking-wider text-mut-2 mt-4 text-center" data-id="element-461">
                Wordmark & mark
              </figcaption>
            </figure>
          </div>
        </section>

        <section className="sec" data-id="element-462">
          <div className="sec__head mb-12" data-id="element-463">
            <h2 data-id="element-464">The Results</h2>
          </div>
          <div className="cards" data-id="element-465">
            <div className="card" data-id="element-466">
              <div className="text-4xl font-bold text-lime mb-4" data-id="element-467">1×</div>
              <h3 className="text-xl mb-2" data-id="element-468">One platform</h3>
              <p data-id="element-469">Bookings, payments & clients</p>
            </div>
            <div className="card" data-id="element-470">
              <div className="text-4xl font-bold text-lime mb-4" data-id="element-471">6 wks</div>
              <h3 className="text-xl mb-2" data-id="element-472">Launch-ready</h3>
              <p data-id="element-473">From kickoff to delivery</p>
            </div>
            <div className="card" data-id="element-474">
              <div className="text-4xl font-bold text-lime mb-4" data-id="element-475">3×</div>
              <h3 className="text-xl mb-2" data-id="element-476">Devices covered</h3>
              <p data-id="element-477">Web, tablet & mobile</p>
            </div>
          </div>
        </section>

        <section className="sec border-t border-line mt-12 pt-24" data-id="element-478">
          <div className="text-center flex flex-col items-center" data-id="element-479">
            <div className="eyebrow mb-6" data-id="element-480">Next Project</div>
            <Link to="/work/tende-pay" className="group inline-flex flex-col items-center" data-id="element-481">
              <h2 className="text-5xl md:text-7xl font-bold tracking-tight mb-10 text-ink group-hover:text-lime transition-colors duration-300" data-id="element-482">
                Tende <span className="serif" data-id="element-483">Pay</span>
              </h2>
            </Link>
            <div className="flex flex-wrap gap-4 justify-center" data-id="element-484">
              <CtaButton to="/#contact" variant="pill" data-id="element-485">
                Book Free Consultation
              </CtaButton>
              <CtaButton to="/#work" variant="ghost" data-id="element-486">
                Back to all work
              </CtaButton>
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-487" />
    </>;
}